import { useEffect, useMemo, useState } from 'react'
import { MessageSquare, Search, ArchiveRestore, User as UserIcon } from 'lucide-react'
import { aiApi } from '../../services/api'
import { useAppShellStore } from '../../stores/appShellStore'
// The AI chat store lives in @os/assistant; reuse it so clicking a row opens
// the same global drawer (AppLayout renders <AIChatPanel/> from this store).
import { useAIStore } from '@os/assistant/web/stores/aiStore'

type Filter = 'active' | 'archived' | 'shared'

interface Row {
  id: string
  title: string
  description?: string | null
  updatedAt?: string
  ownerName?: string
}

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'archived', label: 'Archived' },
  { key: 'shared', label: 'Shared with me' },
]

function fmtDate(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString()
}

export default function AssistantDashboard() {
  const [filter, setFilter] = useState<Filter>('active')
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState('')
  const setAiPanelOpen = useAppShellStore((s) => s.setAiPanelOpen)

  const load = async (f: Filter) => {
    setLoading(true)
    try {
      const res = f === 'shared' ? await aiApi.sharedConversations() : await aiApi.listConversations(f)
      setRows(Array.isArray(res.data) ? res.data : [])
    } catch {
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load(filter) }, [filter])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => `${r.title} ${r.description ?? ''} ${r.ownerName ?? ''}`.toLowerCase().includes(q))
  }, [rows, query])

  const open = (id: string) => {
    useAIStore.getState().selectConversation(id)
    setAiPanelOpen(true)
  }

  const unarchive = async (id: string) => {
    await useAIStore.getState().archiveConversation(id, false)
    load('archived')
  }

  return (
    <div className="h-full flex flex-col bg-background">
      <div className="px-6 py-4 flex flex-col gap-3 min-h-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-md bg-muted p-0.5 text-xs font-medium">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1 rounded transition ${filter === f.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="ml-auto relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search chats…"
              className="pl-7 pr-2 py-1.5 text-xs rounded-md bg-muted text-foreground outline-none w-56 placeholder:text-muted-foreground"
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto rounded-xl border border-border bg-card">
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : visible.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No chats here yet.</div>
          ) : (
            <ul className="divide-y divide-border">
              {visible.map((r) => (
                <li key={r.id} className="flex items-center gap-3 px-4 py-3 hover:bg-accent transition group">
                  <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
                    <MessageSquare size={15} className="text-primary" />
                  </div>
                  <button onClick={() => open(r.id)} className="flex-1 min-w-0 text-left">
                    <div className="text-sm font-medium text-foreground truncate">{r.title || 'New Chat'}</div>
                    {r.description && <div className="text-xs text-muted-foreground truncate">{r.description}</div>}
                  </button>
                  {filter === 'shared' && r.ownerName && (
                    <span className="hidden sm:flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
                      <UserIcon size={12} /> {r.ownerName}
                    </span>
                  )}
                  <span className="text-[11px] text-muted-foreground shrink-0 w-20 text-right">{fmtDate(r.updatedAt)}</span>
                  {filter === 'archived' && (
                    <button
                      onClick={() => unarchive(r.id)}
                      title="Unarchive"
                      className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition shrink-0"
                    >
                      <ArchiveRestore size={14} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
